import { CardGrid } from '@liveauctioneers/hammer-ui-core/card/cardGrid';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    SearchPageItemCard,
    type SearchPageItemCardProps,
} from '@/pages/Search/SearchPage/components/SearchPageTabs/components/SearchPageCardGrid/components/SearchPageItemCard/SearchPageItemCard';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { trackPromotedItemVisible } from '@/redux/modules/analytics';
import { getItemSummaryMap } from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getHasSimilarItemsGroupsFetchCompleted,
    getIsSimilarItemsGroupsLoading,
    getSimilarItemsGroupsItemIds,
} from '@/redux/modules/similarItemsGroups';
import { ItemSummary } from '@/types/item/ItemSummary';

const DEFAULT_SKELETON_COUNT = 12;

type Props = {
    itemId: number;
};

export const SimilarPageCardGrid = ({ itemId }: Props) => {
    const dispatch = useAppDispatch();
    const similarPageItemIds = useAppSelector((state) => getSimilarItemsGroupsItemIds(state, itemId));

    const trackVisible = useCallback(
        (itemData: ItemSummary & { position: number }) => {
            if (itemData.promoted) {
                dispatch(trackPromotedItemVisible({ itemId: itemData.itemId }));
            }
        },
        [dispatch]
    );

    const [startTransition, setStartTransition] = useState(false);

    const itemCardData = useAppSelector((state) => getItemSummaryMap(state, similarPageItemIds));

    const itemCardDataWithPosition = useMemo(() => {
        const result: Record<number, ItemSummary & { position: number }> = {};

        similarPageItemIds.forEach((similarItemId, index) => {
            const itemData = itemCardData[similarItemId];
            if (itemData) {
                result[similarItemId] = {
                    ...itemData,
                    position: index + 1,
                    promoted: Boolean(itemData.promoted),
                };
            }
        });

        return result;
    }, [itemCardData, similarPageItemIds]);

    const isSimilarItemsGroupsLoading = useAppSelector((state) => getIsSimilarItemsGroupsLoading(state, itemId));
    const hasSimilarFetchCompleted = useAppSelector((state) => getHasSimilarItemsGroupsFetchCompleted(state, itemId));

    const forceSkeleton = useMemo(() => {
        const hasData = similarPageItemIds.length > 0 && similarPageItemIds.every((id) => Boolean(itemCardData[id]));
        return !hasSimilarFetchCompleted || isSimilarItemsGroupsLoading || (similarPageItemIds.length > 0 && !hasData);
    }, [hasSimilarFetchCompleted, isSimilarItemsGroupsLoading, itemCardData, similarPageItemIds]);

    useEffect(() => {
        if (!forceSkeleton) {
            requestAnimationFrame(() => {
                setStartTransition(true);
            });
        } else {
            setStartTransition(false);
        }
    }, [forceSkeleton]);

    const itemCardDataForCard = useMemo(() => {
        const result: Record<number, SearchPageItemCardProps> = {};

        Object.entries(itemCardDataWithPosition).forEach(([itemId, itemData]) => {
            result[Number(itemId)] = {
                ...itemData,
                hasNewSimilarItemsIconCTA: true,
            };
        });

        return result;
    }, [itemCardDataWithPosition]);

    const cardGridProps = {
        card: SearchPageItemCard,
        className: cn(
            'min-w-0 gap-y-lg mobile:grid-cols-2 mobile:gap-y-md [&>article]:transition-opacity [&>article]:duration-[300ms] [&>article]:ease-in',
            {
                '[&>article]:opacity-0': !startTransition,
                '[&>article]:opacity-100': startTransition,
            },
            '[&>article>*:last-child]:mt-auto [&>article]:flex [&>article]:h-full [&>article]:min-h-0 [&>article]:flex-col [&>article]:overflow-hidden [&>article]:rounded-md [&>article]:border [&>article]:border-border-secondary'
        ),
        colsByBreakpoint: {
            lg: 4,
            md: 4,
            sm: 3,
            xl: 4,
            xs: 2,
        },
        forceSkeleton,
        gapsByBreakpoint: {
            lg: 'md',
            md: 'md',
            sm: 'sm',
            xl: 'md',
            xs: 'sm',
        } as const,
        onCardVisible: trackVisible,
    };

    return (
        <CardGrid
            {...cardGridProps}
            cardData={itemCardDataForCard}
            cards={similarPageItemIds}
            skeletonCardCount={similarPageItemIds.length > 0 ? similarPageItemIds.length : DEFAULT_SKELETON_COUNT}
        />
    );
};
